/// <reference types="vite/client" />

export interface User {
  _id: string;
  id: string;
  firstName: string;
  lastName: string;
  fullName: string;
  email: string;
  role: 'user' | 'admin' | 'super_admin' | 'ct_team' | 'guest';
  expirationDate?: string;
  lastActiveAt?: string;
  type?: 'smb' | 'workshop' | 'freemium';
  hubspotId?: string;
  slackId?: string;
  lastLoginAt?: string;
  hasSeenIntroduction: boolean;
  hasSeenCustomerAvatarIntro?: boolean;
  hasSeen13weekCashflowIntro?: boolean;
  hasSeenWeeklyCompassIntro?: boolean;
  hasSeenVisionPlanIntro?: boolean;
  hasSeenVisionPlanMigrationMessage?: boolean;
  hasSeenBusinessModelAnalysisIntro?: boolean;
  hasSeenCashflowAccelerationIntro?: boolean;
  hasSeenTwoOars?: boolean;
  hasSeenDuplicateYourselfIntro?: boolean;
  hasAgreedToVerticalIQTerms?: boolean;
  companyLogo?: string;
  companyName?: string;
  currency?: string;
  emailVerified: boolean;
  pendingEmail?: string;
  pendingEmailExpires?: string;
  lastSlackReminderAt?: string;
  slackReminderCount?: number;
  profileCompleted?: boolean; // Cached profile completion status
  profileLastUpdated?: string; // When profile was last updated
  disabled: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface UpdateActivityData {
  hasSeenIntroduction?: boolean;
  hasSeenCustomerAvatarIntro?: boolean;
  hasSeen13weekCashflowIntro?: boolean;
  hasSeenWeeklyCompassIntro?: boolean;
  hasSeenVisionPlanIntro?: boolean;
  hasSeenVisionPlanMigrationMessage?: boolean;
  hasSeenBusinessModelAnalysisIntro?: boolean;
  hasSeenCashflowAccelerationIntro?: boolean;
  hasSeenTwoOars?: boolean;
  hasSeenDuplicateYourselfIntro?: boolean;
  hasAgreedToVerticalIQTerms?: boolean;
  companyLogo?: string;
  companyName?: string;
  currency?: string;
}

interface AuthResponse {
  user: User;
  token: string;
}

export class AuthApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public code?: string,
    public details?: any
  ) {
    super(message);
    this.name = 'AuthApiError';
  }
}

const API_BASE_URL = import.meta.env['VITE_API_URL'] || '';
const TOKEN_KEY = 'authToken';
const USER_KEY = 'authUser';
const SESSION_EXPIRED_KEY = 'sessionExpiredMessage';

class AuthService {
  private token: string | null = null;
  private user: User | null = null;
  private lastActivityTime = Date.now();
  private refreshTimer: ReturnType<typeof setTimeout> | undefined;
  private activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
  private activityListenersAttached = false;

  constructor() {
    this.loadFromStorage();
    if (this.isAuthenticated()) {
      this.setupActivityTracking();
      this.scheduleTokenRefresh();
    }
  }

  private loadFromStorage(): void {
    try {
      
      const token = localStorage.getItem(TOKEN_KEY);
      const userStr = localStorage.getItem(USER_KEY);
      


      if (token && this.isValidJWTFormat(token)) {
        this.token = token;
      } else if (token) {
        console.warn('🔴 AuthService: Invalid token format found in storage, clearing');
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
        return;
      }

      if (userStr) {
        try {
          const user = JSON.parse(userStr);
          if (user && typeof user === 'object' && user.email) {
            this.user = user;

          } else {
            console.warn('🔴 AuthService: Invalid user data in storage, clearing');
            localStorage.removeItem(USER_KEY);
            if (this.token) {
              localStorage.removeItem(TOKEN_KEY);
              this.token = null;
            }
          }
        } catch (parseError) {
          console.error('🔴 AuthService: Failed to parse user from storage:', parseError);
          localStorage.removeItem(USER_KEY);
          if (this.token) {
            localStorage.removeItem(TOKEN_KEY);
            this.token = null;
          }
        }
      }
    } catch (error) {
      console.error('🔴 AuthService: Error loading from storage:', error);
      // Clear potentially corrupted storage
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
    }
  }

  private isValidJWTFormat(token: string): boolean {
    // JWT should have 3 parts separated by dots
    const parts = token.split('.');
    return parts.length === 3 && parts.every(part => part.length > 0);
  }

  private saveToStorage(): void {
    
    if (this.token && this.user) {
      localStorage.setItem(TOKEN_KEY, this.token);
      localStorage.setItem(USER_KEY, JSON.stringify(this.user));

    } else {

      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
    }
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      let errorData: any = {};
      
      try {
        errorData = await response.json();
      } catch {
        // If response isn't JSON, use default error
      }

      // Handle specific registration incomplete case
      if (response.status === 400 && errorData.code === 'REGISTRATION_INCOMPLETE') {
        console.warn('Registration incomplete detected:', errorData.error);
        // Don't logout automatically for this case - user needs to complete registration
        throw new AuthApiError(
          errorData.error || 'Registration incomplete',
          response.status,
          errorData.code,
          errorData.details
        );
      }

      // Handle authentication errors that require logout
      if (response.status === 401 && errorData.requiresReauth) {
        console.warn('Authentication required, logging out:', errorData.error || 'Session invalid');
        this.logout({ expired: true });
        
        // Re-throw error to allow calling code to handle UI feedback
        throw new AuthApiError(
          errorData.error || 'Authentication required',
          response.status,
          errorData.code,
          errorData.details
        );
      }

      // Handle grace period case - token expired but session valid
      // Automatically attempt token refresh and indicate retry is needed
      if (response.status === 401 && errorData.requiresRefresh) {
        console.log('Token expired but session valid, attempting automatic refresh:', errorData.error);
        
        try {
          // Attempt to refresh the token
          await this.refreshToken();
          console.log('Token refresh successful, caller should retry the request');
          
          // Throw a special error that indicates the caller should retry
          throw new AuthApiError(
            'Token refreshed successfully - retry request',
            response.status,
            'TOKEN_REFRESHED_RETRY_NEEDED',
            errorData.details
          );
        } catch (refreshError) {
          console.warn('Token refresh failed:', refreshError);
          
          // If refresh fails, logout and require re-authentication
          this.logout({ expired: true });
          throw new AuthApiError(
            'Session expired and refresh failed. Please log in again.',
            response.status,
            'REFRESH_FAILED',
            errorData.details
          );
        }
      }

      // Handle other authentication errors
      if (response.status === 401) {
        const errorMessage = errorData.error || 'Authentication failed';
        
        // Check for specific account status errors
        if (errorData.code === 'ACCOUNT_DISABLED') {
          this.logout();
          throw new AuthApiError(
            'Your account has been disabled. Please contact support.',
            response.status,
            errorData.code,
            errorData.details
          );
        }
        
        if (errorData.code === 'ACCOUNT_EXPIRED') {
          this.logout();
          throw new AuthApiError(
            'Your account access has expired. Please contact support.',
            response.status,
            errorData.code,
            errorData.details
          );
        }
        
        if (errorData.code === 'SESSION_EXPIRED') {
          this.logout({ expired: true });
          throw new AuthApiError(
            'Your session has expired. Please log in again.',
            response.status,
            errorData.code,
            errorData.details
          );
        }
        
        throw new AuthApiError(
          errorMessage,
          response.status,
          errorData.code,
          errorData.details
        );
      }

      // Handle other HTTP errors
      throw new AuthApiError(
        errorData.error || 'Request failed',
        response.status,
        errorData.code,
        errorData.details
      );
    }

    const data = await response.json();
    return data;
  }

  async login(email: string, password: string): Promise<User> {
    const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, password }),
    });

    const authData = await this.handleResponse<{data: AuthResponse}>(response);
    
    this.token = authData.data.token;
    this.user = authData.data.user;
    this.saveToStorage();
    
    // Setup activity tracking for new session
    this.setupActivityTracking();
    this.scheduleTokenRefresh();
    
    return authData.data.user;
  }

  async register(firstName: string, lastName: string, email: string, password: string): Promise<User> {
    const requestData = { firstName, lastName, email, password };
    
    // Log the request data for debugging (without password)
    console.log('Registration request:', {
      firstName,
      lastName,
      email,
      passwordLength: password.length,
      passwordHasUppercase: /[A-Z]/.test(password),
      passwordHasLowercase: /[a-z]/.test(password),
      passwordHasNumber: /\d/.test(password),
      passwordHasSpecial: /[@$!%*?&]/.test(password)
    });

    const response = await fetch(`${API_BASE_URL}/api/auth/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestData),
    });

    const authData = await this.handleResponse<{data: AuthResponse}>(response);
    
    this.token = authData.data.token;
    this.user = authData.data.user;
    this.saveToStorage();

    // Setup activity tracking for new session
    this.setupActivityTracking();
    this.scheduleTokenRefresh();

    return authData.data.user;
  }

  logout(options: { expired?: boolean } = {}): void {
    this.cleanupActivityTracking();
    this.token = null;
    this.user = null;
    this.saveToStorage();

    if (options.expired) {
      sessionStorage.setItem(SESSION_EXPIRED_KEY, 'Your session has expired. Please log in again.');
    }

    // Redirect to login page if not already there
    if (window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
  }

  getToken(): string | null {
    return this.token;
  }

  getUser(): User | null {
    return this.user;
  }

  isAuthenticated(): boolean {
    return this.token !== null && this.user !== null;
  }

  // Helper method for guest registration to set authentication state
  setAuthenticationData(token: string, user: User): void {
    this.token = token;
    this.user = user;
    this.saveToStorage();
    this.setupActivityTracking();
    this.scheduleTokenRefresh();
  }

  // Check if token is likely expired (simple check - in production you'd want proper JWT parsing)
  isTokenExpired(): boolean {
    // For now, just check if token exists - token expiry validation can be added later
    return !this.token;
  }

  // Check if token is expired or will expire within 2 minutes
  private isTokenExpiredOrExpiring(): boolean {
    if (!this.token) return true;

    try {
      // Basic JWT structure check
      const parts = this.token.split('.');
      if (parts.length !== 3) return true;

      // Decode payload safely with proper base64url decoding
      const payloadPart = parts[1];
      if (!payloadPart) return true;
      
      // Convert base64url to base64: replace '-' with '+', '_' with '/'
      let base64 = payloadPart.replace(/-/g, '+').replace(/_/g, '/');
      
      // Add padding if needed (base64 requires length to be multiple of 4)
      const padding = base64.length % 4;
      if (padding) {
        base64 += '='.repeat(4 - padding);
      }
      
      const payload = JSON.parse(atob(base64));
      
      // Coerce exp to number and validate
      const expValue = Number(payload.exp);
      if (!Number.isFinite(expValue)) return false;
      
      // Normalize to integer seconds
      const normalizedExp = Math.floor(expValue);
      
      const currentTime = Math.floor(Date.now() / 1000);
      const remaining = normalizedExp - currentTime;
      
      // Check if token expires within the next 2 minutes (120 seconds)
      return remaining <= 120;
    } catch {
      // Return false on any decode/parse error to avoid tight refresh loops
      return false;
    }
  }

  // Helper method to attempt token refresh on app startup
  async attemptTokenRefresh(): Promise<void> {
    if (!this.token) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/refresh-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        
        // Handle grace period case - token expired but session valid
        if (errorData.code === 'TOKEN_EXPIRED_REFRESH_AVAILABLE' || errorData.requiresRefresh) {
          console.log('Token expired but session valid, attempting refresh');
          // Don't throw error for this case, let the refresh attempt continue below
        } else if (errorData.requiresReauth || 
                   errorData.code === 'ACCOUNT_DISABLED' || 
                   errorData.code === 'ACCOUNT_EXPIRED' ||
                   errorData.code === 'SESSION_EXPIRED') {
          throw new Error(errorData.error || 'Session expired');
        } else {
          throw new Error('Refresh failed');
        }
      }

      const authData = await response.json();
      
      if (authData.success && authData.data) {
        this.token = authData.data.token;
        this.user = authData.data.user;
        this.saveToStorage();
        this.setupActivityTracking();
        this.scheduleTokenRefresh();
        if (import.meta.env.DEV || import.meta.env.MODE === 'development' || import.meta.env.MODE === 'staging') {
          console.debug('Token refreshed successfully on startup');
        }
      } else {
        throw new Error('Invalid refresh response');
      }

    } catch (error) {
      console.warn('Startup token refresh failed:', error);
      
      // If this is a registration incomplete error, don't treat it as a critical error
      if (error instanceof AuthApiError && error.code === 'REGISTRATION_INCOMPLETE') {
        console.log('Registration incomplete during token refresh - user needs to complete signup');
        // Clear stored auth data to prevent further automatic attempts
        this.logout();
        // Re-throw the error but don't treat it as a critical failure
        throw new Error('Registration incomplete. Please complete your account setup.');
      }
      
      throw error;
    }
  }

  async updateActivity(data: UpdateActivityData): Promise<User> {
    if (import.meta.env.DEV) {
      console.log('🟣 AuthService: Starting updateActivity', {
        hasToken: !!this.token,
        dataKeys: Object.keys(data),
        hasCompanyLogo: !!data.companyLogo,
        logoLength: data.companyLogo?.length
      });
    }
    
    // Helper function to make the request
    const makeRequest = async (): Promise<Response> => {
      return await fetch(`${API_BASE_URL}/api/auth/activity`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.token}`,
        },
        body: JSON.stringify(data),
      });
    };

    let response = await makeRequest();

    if (import.meta.env.DEV) {
      console.log('🟣 AuthService: updateActivity response', {
        status: response.status,
        ok: response.ok,
        statusText: response.statusText
      });
    }

    try {
      const result = await this.handleResponse<{ data: { user: User } }>(response);
      
      if (import.meta.env.DEV) {
        console.log('🟣 AuthService: updateActivity result', {
          hasData: !!result.data,
          hasUser: !!result.data?.user,
          userEmail: result.data?.user?.email,
          hasCompanyLogo: !!result.data?.user?.companyLogo
        });
      }
      
      this.user = result.data.user;
      this.saveToStorage();

      if (import.meta.env.DEV) {
        console.log('🟣 AuthService: updateActivity completed successfully');
      }
      return result.data.user;
    } catch (error) {
      // If token was refreshed, retry the request once
      if (error instanceof AuthApiError && error.code === 'TOKEN_REFRESHED_RETRY_NEEDED') {
        console.log('🔄 AuthService: Retrying updateActivity after token refresh');
        
        // Make the request again with the new token
        response = await makeRequest();
        
        if (import.meta.env.DEV) {
          console.log('🟣 AuthService: updateActivity retry response', {
            status: response.status,
            ok: response.ok,
            statusText: response.statusText
          });
        }
        
        const result = await this.handleResponse<{ data: { user: User } }>(response);
        
        if (import.meta.env.DEV) {
          console.log('🟣 AuthService: updateActivity retry result', {
            hasData: !!result.data,
            hasUser: !!result.data?.user,
            userEmail: result.data?.user?.email,
            hasCompanyLogo: !!result.data?.user?.companyLogo
          });
        }
        
        this.user = result.data.user;
        this.saveToStorage();

        if (import.meta.env.DEV) {
          console.log('🟣 AuthService: updateActivity retry completed successfully');
        }
        return result.data.user;
      }
      
      // Re-throw other errors
      throw error;
    }
  }

  async forgotPassword(email: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/api/auth/forgot-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    });

    await this.handleResponse<void>(response);
  }

  async resetPassword(token: string, password: string): Promise<User> {
    const response = await fetch(`${API_BASE_URL}/api/auth/reset-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ token, password }),
    });

    const authData = await this.handleResponse<{data: AuthResponse}>(response);
    
    this.token = authData.data.token;
    this.user = authData.data.user;
    this.saveToStorage();

    return authData.data.user;
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<User> {
    // Debug logging
    console.log('Change password debug:', {
      hasToken: !!this.token,
      tokenLength: this.token?.length,
      isAuthenticated: this.isAuthenticated(),
      hasUser: !!this.user
    });

    if (!this.token) {
      throw new Error('No authentication token available. Please log in again.');
    }

    const response = await fetch(`${API_BASE_URL}/api/auth/change-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.token}`,
      },
      body: JSON.stringify({ currentPassword, newPassword }),
    });

    const result = await this.handleResponse<{ user: User }>(response);
    this.user = result.user;
    this.saveToStorage();

    return result.user;
  }

  updateUserData(user: User): void {
    this.user = user;
    this.saveToStorage();
  }

  async refreshUserData(): Promise<User> {
    if (!this.token) {
      throw new AuthApiError('No authentication token available', 401);
    }

    // Helper function to make the request
    const makeRequest = async (): Promise<Response> => {
      return await fetch(`${API_BASE_URL}/api/auth/me`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${this.token}`,
          'Content-Type': 'application/json',
        },
      });
    };

    let response = await makeRequest();

    try {
      const result = await this.handleResponse<{ data: { user: User } }>(response);
      
      this.user = result.data.user;
      this.saveToStorage();
      
      return result.data.user;
    } catch (error) {
      // If token was refreshed, retry the request once
      if (error instanceof AuthApiError && error.code === 'TOKEN_REFRESHED_RETRY_NEEDED') {
        console.log('🔄 AuthService: Retrying refreshUserData after token refresh');
        
        // Make the request again with the new token
        response = await makeRequest();
        const result = await this.handleResponse<{ data: { user: User } }>(response);
        
        this.user = result.data.user;
        this.saveToStorage();
        
        return result.data.user;
      }
      
      // Re-throw other errors
      throw error;
    }
  }

  private setupActivityTracking(): void {
    if (this.activityListenersAttached) return;

    this.activityListenersAttached = true;

    this.activityEvents.forEach((event) => {
      document.addEventListener(event, this.activityHandler.bind(this));
    });
  }

  private activityHandler(_event: Event): void {
    this.lastActivityTime = Date.now();
  }

  private scheduleTokenRefresh(): void {
    if (this.refreshTimer) clearTimeout(this.refreshTimer);

    // Check if token is already expired or will expire soon
    if (this.isTokenExpiredOrExpiring()) {
      if (import.meta.env.DEV || import.meta.env.MODE === 'development' || import.meta.env.MODE === 'staging') {
        console.debug('Token is expired/expiring, scheduling immediate refresh');
      }
      this.refreshTimer = setTimeout(() => {
        this.refreshToken();
      }, 100); // Immediate refresh
      return;
    }

    const refreshInterval = 1000 * 60 * 3; // 3 minutes (more frequent)
    const timeSinceLastActivity = Date.now() - this.lastActivityTime;
    
    // If user has been inactive for more than 30 minutes, don't schedule refresh
    if (timeSinceLastActivity > 1000 * 60 * 30) { // 30 minutes
      if (import.meta.env.DEV || import.meta.env.MODE === 'development' || import.meta.env.MODE === 'staging') {
        console.debug('User inactive for too long, not scheduling token refresh');
      }
      return;
    }

    const timeUntilRefresh = Math.max(refreshInterval, refreshInterval - timeSinceLastActivity);

    if (import.meta.env.DEV || import.meta.env.MODE === 'development' || import.meta.env.MODE === 'staging') {
      console.debug(`Scheduling token refresh in ${Math.round(timeUntilRefresh / 1000 / 60)} minutes`);
    }

    this.refreshTimer = setTimeout(() => {
      this.refreshToken();
    }, timeUntilRefresh);
  }

  private async refreshToken(): Promise<void> {
    if (!this.token) return;

    try {
      const timeSinceLastActivity = Date.now() - this.lastActivityTime;
      const activityThreshold = 1000 * 60 * 30; // 30 minutes

      // Don't refresh if user hasn't been active recently
      if (timeSinceLastActivity > activityThreshold) {
        if (import.meta.env.DEV || import.meta.env.MODE === 'development' || import.meta.env.MODE === 'staging') {
          console.debug('User inactive, skipping token refresh');
        }
        this.scheduleTokenRefresh();
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/auth/refresh-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        
        // Handle grace period case - token expired but session valid
        if (errorData.code === 'TOKEN_EXPIRED_REFRESH_AVAILABLE' || errorData.requiresRefresh) {
          console.log('Token expired but session valid during refresh, continuing...');
          // Don't return early, let the refresh attempt continue below
        } else if (errorData.requiresReauth || 
                   errorData.code === 'ACCOUNT_DISABLED' || 
                   errorData.code === 'ACCOUNT_EXPIRED' ||
                   errorData.code === 'SESSION_EXPIRED') {
          console.warn('Session invalidated:', errorData.error || 'Unknown error');
          this.logout({ expired: errorData.code === 'SESSION_EXPIRED' });
          return;
        } else {
          // For other errors, schedule next refresh
          console.warn('Token refresh failed, will retry:', errorData.error);
          this.scheduleTokenRefresh();
          return;
        }
      }

      const authData = await response.json();
      
      if (authData.success && authData.data) {
        this.token = authData.data.token;
        this.user = authData.data.user;
        this.saveToStorage();
        if (import.meta.env.DEV || import.meta.env.MODE === 'development' || import.meta.env.MODE === 'staging') {
          console.debug('Token refreshed successfully');
        }
      } else {
        console.warn('Token refresh response was not successful');
      }

    } catch (error) {
      console.warn('Token refresh failed:', error);
    } finally {
      this.scheduleTokenRefresh();
    }
  }

  private cleanupActivityTracking(): void {
    if (!this.activityListenersAttached) return;

    this.activityEvents.forEach((event) => {
      document.removeEventListener(event, this.activityHandler.bind(this));
    });

    this.activityListenersAttached = false;

    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = undefined;
    }
  }
}

// Export singleton instance
export const authService = new AuthService(); 